import { ChangeDetectionStrategy, Component, computed, ElementRef, EventEmitter, inject, input, Output, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { InputPositiveIntegerDirective } from '../shared/directives/input-positive-integer/input-positive-integer.directive';
import { BatchWeighingDomainService } from '../shared/domain/services/batch-weighings/batch-weighing.domain.service';
import { DataStoreReaderService } from '../shared/services/data-store/viewmodels/data-store-reader-viewmodels.service';
import { Uuid } from '../shared/domain/model/uuid/uuid.type';
import { BatchWeighingComponent } from '../batch-weighing/batch-weighing.component';
import { UpdateInfoViewModel } from '../shared/viewmodels/shared/update.viewmodel';
import { AnimalWeighingSaveInfoViewModel } from '../shared/viewmodels/shared/animal-weighing-save-info.viewmodel';
import { AnimalWeightsViewModel } from '../shared/viewmodels/shared/animal-weights.viewmodel';
import { StoreUpdateInfo } from '../shared/storage/store-update-info';

@Component({
  selector: 'app-reg-animal-weights',
  imports: [ReactiveFormsModule, InputPositiveIntegerDirective, MatInputModule],
  templateUrl: './reg-animal-weights.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegAnimalWeightsComponent {
  readonly animalWeighingId = input.required<Uuid>();
  private readonly viewModelGetter = inject(DataStoreReaderService);
  private readonly batchWeighingComponent = inject(BatchWeighingComponent);
  protected animalWeighingViewModel = computed(() => this.viewModelGetter.getAnimalWeighingViewModels(this.animalWeighingId()));
  protected isOutlier = signal(false);
  @Output() inputFormChanged = new EventEmitter();

  private originalNumberOfAnimals: number = 0;
  private originalAnimalsWeight: number = 0;

  private readonly numberOfAnimalsControl: FormControl<number | string>;
  private readonly animalsWeightControl: FormControl<number | string>;
  protected readonly inputForm: FormGroup<{
    numberOfAnimals: FormControl<number | string>;
    animalsWeight: FormControl<number | string>;
  }>;

  constructor(
    private fb: FormBuilder,
    private doc: ElementRef,
    protected weighingIntialDomainService: BatchWeighingDomainService
  ) {
    this.numberOfAnimalsControl = fb.nonNullable.control<number | string>('');
    this.animalsWeightControl = fb.nonNullable.control<number | string>('');

    this.inputForm = fb.nonNullable.group({
      numberOfAnimals: this.numberOfAnimalsControl,
      animalsWeight: this.animalsWeightControl,
    });
    this.inputForm.valueChanges.subscribe(() => {
      setTimeout(() => {
        this.inputFormChanged.emit({
          animals: this.getNumberOfAnimals(),
          weight: this.getWeight()
        });
      }, 0);
    });
  }

  ngOnChanges() {
    this.originalNumberOfAnimals = this.animalWeighingViewModel().inputNumber;
    this.originalAnimalsWeight = this.animalWeighingViewModel().inputWeight;
    this.numberOfAnimalsControl.setValue(this.getInputValue(this.animalWeighingViewModel().inputNumber));
    this.animalsWeightControl.setValue(this.getInputValue(this.animalWeighingViewModel().inputWeight));
  }

  private getInputValue(value: number): number | string {
    return value === 0 ? '' : value;
  }

  isDirty(): boolean {
    return this.inputForm.dirty && !this.isOriginalValues();
  }

  isOriginalValues(): boolean {
    return (
      this.getNumberOfAnimals() === this.originalNumberOfAnimals && this.getWeight() === this.originalAnimalsWeight
    );
  }

  private getNumberOfAnimals(): number {
    return this.convertToNumber(this.numberOfAnimalsControl.value);
  }

  private convertToNumber(value: unknown): number {
    let output = 0;
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      output = Number(value);
    }
    if (Number.isNaN(output)) {
      return 0;
    }
    return output ?? 0;
  }

  // private isString(value: unknown): boolean {
  //   if (typeof value === 'string') {
  //     return true;
  //   }
  //   return false;
  // }

  private getWeight(): number {
    return this.convertToNumber(this.animalsWeightControl.value);
  }

  protected getAverageWeight(): number {
    return this.weighingIntialDomainService.averageWeightPerPenRounded(this.getNumberOfAnimals(), this.getWeight());
  }

  getAnimalWeightsViewModel(): AnimalWeightsViewModel {
    return new AnimalWeightsViewModel(this.getNumberOfAnimals(), this.getWeight());
  }

  animalWeighingSaveInfoViewModel(): AnimalWeighingSaveInfoViewModel {
    return new AnimalWeighingSaveInfoViewModel(
      this.animalWeighingViewModel().id,
      this.animalWeighingViewModel().batchWeighingId,
      this.getAnimalWeightsViewModel(),
      this.animalWeighingViewModel().update
    );
  }

  makePristine(): void {
    this.originalNumberOfAnimals = this.getNumberOfAnimals();
    this.originalAnimalsWeight = this.getWeight();
    this.inputForm.markAsPristine();
  }

  mapUpdateToSaveUpdate(updatevm: UpdateInfoViewModel): StoreUpdateInfo {
    return new StoreUpdateInfo(updatevm.lastServerUpdate, updatevm.lastLocalUpdate, updatevm.lastEditedByUser);
  }

  setOutlier(): void {
    this.isOutlier.set(this.batchWeighingComponent.isOutlierWeightForPen(this.getAnimalWeightsViewModel()));
  }
}
